import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { ShieldAlert, CheckCircle, Search, Edit3, X, Save, Clock, XCircle } from 'lucide-react';

const ReportedTransactions = ({ userRole }) => {
    const [reports, setReports] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filter, setFilter] = useState('pending_review'); // 'pending_review', 'investigating', 'resolved', 'rejected', 'all'
    const [selectedReport, setSelectedReport] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);

    // Form state for the modal
    const [adminNotes, setAdminNotes] = useState('');
    const [newStatus, setNewStatus] = useState('');

    useEffect(() => {
        fetchReports();
    }, []);

    const fetchReports = async () => {
        setIsLoading(true);
        try {
            // We fetch the reports and do a join on auth.users to get the email (if your DB allows it)
            // If you can't join auth.users due to permissions, you can just display the user_id.
            const { data, error } = await supabase
                .from('reported_transactions')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setReports(data || []);
        } catch (error) {
            console.error("Error fetching reported transactions:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = (report) => {
        setSelectedReport(report);
        setAdminNotes(report.admin_notes || '');
        setNewStatus(report.status);
    };

    const handleUpdateReport = async () => {
        if (!selectedReport) return;
        setIsUpdating(true);

        try {
            // 1. Update the transaction report status
            const { error: updateError } = await supabase
                .from('reported_transactions')
                .update({ 
                    status: newStatus,
                    admin_notes: adminNotes,
                })
                .eq('id', selectedReport.id);

            if (updateError) throw updateError;

            // 2. Fetch the client's email and name for the automations from the Master Broker table
            const { data: clientProfile } = await supabase
                .from('user_broker_accounts') 
                .select('email, name, first_name, last_name')
                // Checks the legacy user_id or the primary id column to map the auth UUID
                .or(`user_id.eq.${selectedReport.user_id},id.eq.${selectedReport.user_id}`)
                .maybeSingle();

            const targetEmail = clientProfile?.email;
            const targetName = clientProfile?.name || clientProfile?.first_name || 'Client';

            // 3. Trigger Automations ONLY if the dispute is Resolved or Rejected
            if (targetEmail && (newStatus === 'resolved' || newStatus === 'rejected')) {
                const isResolved = newStatus === 'resolved';
                const subject = isResolved 
                    ? `Update: Your Dispute for Transaction ${selectedReport.transaction_id.substring(0, 8)} is Resolved` 
                    : `Update: Your Dispute for Transaction ${selectedReport.transaction_id.substring(0, 8)} was Rejected`;
                
                const messageBody = isResolved
                    ? `Dear ${targetName},\n\nOur compliance team has investigated your flagged transaction (${selectedReport.transaction_id}). We have resolved the issue in your favor and secured your account. Please check your ledger for updates.`
                    : `Dear ${targetName},\n\nOur compliance team has investigated your flagged transaction (${selectedReport.transaction_id}). Based on blockchain ledgers and IP verification, we have determined this was a legitimate transaction and the dispute has been rejected.`;

                // A. Dispatch the Automated Email
                await supabase.functions.invoke('send-branded-email', {
                    body: {
                        clientData: { email: targetEmail, name: targetName },
                        agentName: 'Nommia Compliance Team',
                        customMessage: messageBody.replace(/\n/g, '<br/>'),
                        emailSubject: subject 
                    }
                });

                // B. Log into the Client's Communication Inbox (ClientProfile Comms Tab)
                const threadId = `DISP-${Math.floor(Math.random() * 90000) + 10000}`;
                await supabase.from('crm_communications').insert([{
                    id: threadId,
                    client_id: selectedReport.user_id,
                    client_email: targetEmail,
                    client_name: targetName,
                    type: 'email',
                    direction: 'outbound',
                    subject: subject,
                    body: messageBody,
                    agent_name: 'System (Compliance Auto)',
                    status: 'Sent'
                }]);
            }

            // 4. Log the action into the Global System Audit (Regardless of status)
            if (targetEmail) {
                await supabase.from('crm_system_logs').insert([{
                    client_email: targetEmail,
                    action: `Dispute ${newStatus.toUpperCase()}`,
                    description: `Agent updated dispute ${selectedReport.id}. Notes: ${adminNotes ? adminNotes.substring(0, 50) + '...' : 'None'}`,
                    actor: userRole || 'Compliance Officer',
                    type: 'security'
                }]);
            }

            // 5. Update local state instantly
            setReports(prev => prev.map(r => r.id === selectedReport.id ? { ...r, status: newStatus, admin_notes: adminNotes } : r));
            setSelectedReport(null);
            alert(`Report updated successfully. ${newStatus === 'resolved' || newStatus === 'rejected' ? 'Automated email dispatched and logged.' : 'Audit log updated.'}`);

        } catch (error) {
            console.error("Error updating report:", error);
            alert("Failed to update report: " + error.message);
        } finally {
            setIsUpdating(false);
        }
    };

    const filteredReports = reports.filter(r => filter === 'all' || r.status === filter);

    const getStatusColor = (status) => {
        switch(status) {
            case 'pending_review': return 'bg-amber-500/20 text-amber-500 border-amber-500/30';
            case 'investigating': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
            case 'resolved': return 'bg-green-500/20 text-green-400 border-green-500/30';
            case 'rejected': return 'bg-red-500/20 text-red-400 border-red-500/30';
            default: return 'bg-slate-800 text-slate-400 border-slate-700';
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header & Filters */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-slate-900 p-6 rounded-2xl border border-slate-800">
                <div>
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <ShieldAlert className="text-red-500" /> Transaction Disputes
                    </h3>
                    <p className="text-sm text-slate-400 mt-1">Review and resolve transactions flagged by clients.</p>
                </div>

                <div className="flex bg-slate-950 p-1 rounded-lg border border-slate-800">
                    {['pending_review', 'investigating', 'resolved', 'rejected', 'all'].map(status => (
                        <button 
                            key={status}
                            onClick={() => setFilter(status)} 
                            className={`px-4 py-2 rounded-md text-xs font-bold transition-all capitalize ${filter === status ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                        >
                            {status.replace('_', ' ')}
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Table */}
            <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase border-b border-slate-800">
                            <tr>
                                <th className="px-6 py-4">Date Reported</th>
                                <th className="px-6 py-4">Client ID</th>
                                <th className="px-6 py-4">Type</th>
                                <th className="px-6 py-4 text-right">Amount</th>
                                <th className="px-6 py-4 text-center">Status</th>
                                <th className="px-6 py-4 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800 text-sm">
                            {isLoading ? (
                                <tr><td colSpan="6" className="p-8 text-center text-slate-500">Loading reports...</td></tr>
                            ) : filteredReports.length === 0 ? (
                                <tr><td colSpan="6" className="p-12 text-center text-slate-500 italic">No reports found for this filter.</td></tr>
                            ) : (
                                filteredReports.map(report => (
                                    <tr key={report.id} className="hover:bg-slate-800/50 transition-colors cursor-pointer" onClick={() => handleOpenModal(report)}>
                                        <td className="px-6 py-4 text-slate-400 text-xs whitespace-nowrap">
                                            {new Date(report.created_at).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="font-mono text-xs text-slate-300 bg-slate-950 px-2 py-1 rounded border border-slate-800 truncate block max-w-[120px]" title={report.user_id}>
                                                {report.user_id.substring(0, 8)}...
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 font-bold text-white uppercase">{report.transaction_type}</td>
                                        <td className="px-6 py-4 text-right font-mono font-bold text-amber-500">{report.amount}</td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase border whitespace-nowrap ${getStatusColor(report.status)}`}>
                                                {report.status.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button className="text-blue-400 hover:text-blue-300 font-bold text-xs flex items-center justify-end gap-1 ml-auto">
                                                Review <Edit3 size={12} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Resolution Modal */}
            {selectedReport && (
                <div className="fixed inset-0 bg-slate-950/90 z-[100] flex items-center justify-center p-4 animate-fade-in backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl shadow-2xl p-6 relative">
                        <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-800">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                <ShieldAlert className="text-amber-500" /> Dispute Resolution
                            </h3>
                            <button onClick={() => setSelectedReport(null)} disabled={isUpdating} className="text-slate-400 hover:text-white disabled:opacity-50"><X size={20}/></button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                                <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">Transaction ID (Reference)</p>
                                <div className="flex items-center gap-2">
                                    <p className="font-mono text-xs text-blue-400 break-all">{selectedReport.transaction_id}</p>
                                </div>
                            </div>
                            <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                                <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">Disputed Amount</p>
                                <p className="text-lg font-bold text-amber-500 font-mono">{selectedReport.amount}</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Update Investigation Status</label>
                                <select 
                                    className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                                    value={newStatus}
                                    onChange={(e) => setNewStatus(e.target.value)}
                                >
                                    <option value="pending_review">Pending Review</option>
                                    <option value="investigating">Investigating (In Progress)</option>
                                    <option value="resolved">Resolved (Refunded/Fixed)</option>
                                    <option value="rejected">Rejected (Legitimate Transaction)</option>
                                </select>
                            </div>

                            <div>
                                <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Internal Admin Notes</label>
                                <textarea 
                                    className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none min-h-[120px]"
                                    placeholder="Log your findings from the blockchain explorer or payment processor here..."
                                    value={adminNotes}
                                    onChange={(e) => setAdminNotes(e.target.value)}
                                ></textarea>
                                <p className="text-[10px] text-slate-500 mt-1">These notes are strictly internal and will not be seen by the client.</p>
                            </div>
                        </div>

                        <div className="pt-6 mt-6 border-t border-slate-800 flex gap-3">
                            <button onClick={() => setSelectedReport(null)} disabled={isUpdating} className="flex-1 py-3 bg-slate-800 text-slate-300 font-bold rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors">Cancel</button>
                            <button 
                                onClick={handleUpdateReport} 
                                disabled={isUpdating || newStatus === selectedReport.status && adminNotes === (selectedReport.admin_notes || '')}
                                className="flex-1 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-500 flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                            >
                                {isUpdating ? 'Saving...' : <><Save size={16}/> Save Updates</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportedTransactions;